// app/src/lib/cashflow/insurance.js — detect insurance premiums from bank activity.
//
// Restaurants carry several policies (general liability, property, workers' comp,
// liquor liability…). Premiums are paid in regular installments (monthly or
// quarterly) and the carrier raises the rate roughly ONCE a year — which shows
// up as a single installment stepping UP from the prior one. We:
//   - classify insurance outflows by user keyword (default "Farmers"),
//   - total them by window (YTD / trailing 12 months),
//   - and surface the most recent rate step-up (its date + %), which is
//     effectively the annual increase ("one payment up from the prior quarter").
// Same keyword model + matcher as cashflow/rent + cashflow/taxes. Storage lives
// on userProfile.insuranceKeywords.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';

const MAX_KEYWORDS = 25;
const DEFAULT_KEYWORDS = ['Farmers']; // common carrier; user-editable
const INCREASE_THRESHOLD = 0.005;     // ignore <0.5% wobble (rounding / small fees)

export async function getKeywords() {
  const c = await currentClaims();
  if (!c) return DEFAULT_KEYWORDS.slice();
  const p = await db.userProfile.get(c.sub);
  return (p && Array.isArray(p.insuranceKeywords) && p.insuranceKeywords.length)
    ? p.insuranceKeywords : DEFAULT_KEYWORDS.slice();
}

export async function setKeywords(list) {
  const c = await currentClaims();
  if (!c) throw new Error('insurance: not signed in');
  const clean = [...new Set((list || []).map((s) => String(s).trim()).filter(Boolean))].slice(0, MAX_KEYWORDS);
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  await db.userProfile.put({ ...existing, userId: c.sub, insuranceKeywords: clean });
  return clean;
}

function escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function matchesKeyword(hay, k) {
  const key = String(k || '').toLowerCase();
  if (!key) return false;
  if (/[^\w ]/.test(key)) return hay.includes(key);
  return new RegExp('\\b' + escapeRegex(key), 'i').test(hay);
}

export function isInsurance(tx, keywords) {
  if (!tx || tx.pending) return false;
  if ((tx.amountCents || 0) <= 0) return false; // outflows only
  if (!keywords || keywords.length === 0) return false;
  const hay = ((tx.merchantName || '') + ' ' + (tx.description || '')).toLowerCase();
  return keywords.some((k) => matchesKeyword(hay, k));
}

/**
 * Summarize insurance payments across the provided transactions.
 * @param {Array} txs        plaid transactions (any window)
 * @param {string[]} keywords detection keywords
 * @param {Date} [today]     reference "now" for YTD / trailing-12 windows
 * @returns {{
 *   payments: Array<{date:string, amountCents:number, merchant:string}>,  // ascending by date
 *   totalCents:number, ytdCents:number, ttmCents:number,
 *   increases: Array<{date:string, fromCents:number, toCents:number, pctIncrease:number}>,
 *   latestIncrease: {date:string, fromCents:number, toCents:number, pctIncrease:number}|null,
 * }}
 */
export function summarize(txs, keywords, today = new Date()) {
  const payments = [];
  for (const tx of txs || []) {
    // Explicit Spending-tab categorization wins both ways (see rent.summarize).
    if (tx._spendCat ? tx._spendCat !== 'insurance' : !isInsurance(tx, keywords)) continue;
    if (!tx.date) continue;
    payments.push({
      date: tx.date,
      amountCents: tx.amountCents || 0,
      merchant: tx.merchantName || tx.description || 'Insurance',
    });
  }
  payments.sort((a, b) => a.date.localeCompare(b.date)); // ascending

  const yStr = `${today.getFullYear()}-`;
  // First day of the month 11 months ago → a month-aligned trailing-12 window.
  const tStart = new Date(today.getFullYear(), today.getMonth() - 11, 1);
  const ttmStart = `${tStart.getFullYear()}-${String(tStart.getMonth() + 1).padStart(2, '0')}-01`;

  let totalCents = 0, ytdCents = 0, ttmCents = 0;
  for (const p of payments) {
    totalCents += p.amountCents;
    if (p.date.startsWith(yStr)) ytdCents += p.amountCents;
    if (p.date >= ttmStart) ttmCents += p.amountCents;
  }

  // Rate step-ups: a payment whose amount rises more than the threshold over the
  // immediately preceding insurance payment. The most recent one is the year's
  // rate increase.
  const increases = [];
  for (let i = 1; i < payments.length; i += 1) {
    const prev = payments[i - 1], cur = payments[i];
    if (prev.amountCents > 0 && (cur.amountCents - prev.amountCents) / prev.amountCents > INCREASE_THRESHOLD) {
      increases.push({
        date: cur.date,
        fromCents: prev.amountCents,
        toCents: cur.amountCents,
        pctIncrease: ((cur.amountCents - prev.amountCents) / prev.amountCents) * 100,
      });
    }
  }
  const latestIncrease = increases.length ? increases[increases.length - 1] : null;

  return { payments, totalCents, ytdCents, ttmCents, increases, latestIncrease };
}
